import { FC, useEffect, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import { Spinner, Badge } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { GET_MY_NOTIFICATIONS } from '../../../../app/tfi/gql/queries/notificationsQuery'
import {
  MARK_NOTIFICATION_AS_READ,
  MARK_ALL_NOTIFICATIONS_AS_READ,
  DELETE_NOTIFICATION,
} from '../../../../app/tfi/gql/mutations/notificationMutations'
import {
  IGetMyNotificationsResponse,
  IMarkNotificationAsReadResponse,
  IMarkAllNotificationsAsReadResponse,
  IDeleteNotificationResponse,
  INotification,
  NotificationType,
  SIGNATURE_NOTIFICATION_TYPES,
} from '../../../../app/tfi/types/notifications'
import { toAbsoluteUrl } from '../../../helpers'
import { formatDistanceToNow } from 'date-fns'
import { parseId } from '../../../../app/tfi/helpers'
import { useRealtimeStore } from '../../../../app/tfi/store/realtimeStore'
import {defineMessages, useIntl} from 'react-intl'
import {NOTIFICATION_MESSAGES as N, priorityLabel} from './notificationMessages'

const LOCAL = defineMessages({
  emptyTitle: {id: 'shell.notifications.signature.emptyTitle', defaultMessage: 'No Signature Notifications'},
  emptyAll: {
    id: 'shell.notifications.signature.emptyAll',
    defaultMessage: 'You have no signature notifications.',
  },
  emptyUnread: {
    id: 'shell.notifications.signature.emptyUnread',
    defaultMessage: 'You have no unread signature notifications.',
  },
  emptyRead: {
    id: 'shell.notifications.signature.emptyRead',
    defaultMessage: 'You have no read signature notifications.',
  },
})

const HeaderSignatureNotificationsMenu: FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'read'>('all')
  const syncUnreadNotificationsByType = useRealtimeStore((state) => state.syncUnreadNotificationsByType)
  const markNotificationRead = useRealtimeStore((state) => state.markNotificationRead)
  const markNotificationDeleted = useRealtimeStore((state) => state.markNotificationDeleted)
  const markAllNotificationsRead = useRealtimeStore((state) => state.markAllNotificationsRead)

  // Fetch signature notifications only
  const { data, loading, error, refetch } = useQuery<IGetMyNotificationsResponse>(
    GET_MY_NOTIFICATIONS,
    {
      variables: {
        first: 50,
        offset: 0,
      },
      fetchPolicy: 'network-only',
      pollInterval: 300000, // Poll every 5 minutes
    }
  )

  // Filter only signature notifications
  const signatureNotifications =
    data?.allNotifications?.edges
      ?.map((edge) => edge.node)
      ?.filter((notif) => SIGNATURE_NOTIFICATION_TYPES.includes(notif.notificationType)) || []

  // Filter by read status based on active tab
  const filteredNotifications = signatureNotifications.filter((notif) => {
    if (activeTab === 'unread') return !notif.isRead
    if (activeTab === 'read') return notif.isRead
    return true // 'all' shows everything
  })

  const unreadCount = signatureNotifications.filter((n) => !n.isRead).length
  const readCount = signatureNotifications.filter((n) => n.isRead).length

  const [markAsRead] = useMutation<IMarkNotificationAsReadResponse>(MARK_NOTIFICATION_AS_READ)
  const [markAllAsRead] = useMutation<IMarkAllNotificationsAsReadResponse>(
    MARK_ALL_NOTIFICATIONS_AS_READ
  )
  const [deleteNotification] = useMutation<IDeleteNotificationResponse>(DELETE_NOTIFICATION)

  useEffect(() => {
    if (error) {
      toast.error(intl.formatMessage(N.errorLoading, {message: error.message}))
    }
  }, [error])

  useEffect(() => {
    if (!data?.allNotifications?.edges) return

    const unreadByTypeMap = new Map<string, number>()
    data.allNotifications.edges.forEach(({node}) => {
      if (!node.isRead) {
        unreadByTypeMap.set(
          node.notificationType,
          (unreadByTypeMap.get(node.notificationType) || 0) + 1
        )
      }
    })

    syncUnreadNotificationsByType(
      Array.from(unreadByTypeMap.entries()).map(([notificationType, unreadCount]) => ({
        notificationType,
        unreadCount,
      }))
    )
  }, [data, syncUnreadNotificationsByType])

  const handleNotificationClick = async (notification: INotification) => {
    // Mark as readed
    if (!notification.isRead) {
      try {
        await markAsRead({
          variables: { notificationId: parseInt(String(parseId(notification.id))) },
        })
        markNotificationRead(notification.notificationType)
        refetch()
      } catch (err: any) {
        toast.error(intl.formatMessage(N.errorMarkingRead, {message: err.message}))
      }
    }

    // Navigate to action URL if exists
    if (notification.actionUrl) {
      navigate(notification.actionUrl)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      const result = await markAllAsRead()
      if (result.data?.markAllNotificationsAsRead.success) {
        markAllNotificationsRead()
        toast.success(
          intl.formatMessage(N.markedAsRead, {count: result.data.markAllNotificationsAsRead.count})
        )
        refetch()
      }
    } catch (err: any) {
      toast.error(intl.formatMessage(N.errorMarkingAllRead, {message: err.message}))
    }
  }

  const handleDelete = async (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const notificationToDelete = signatureNotifications.find((item) => item.id === notificationId)
      const result = await deleteNotification({
        variables: { notificationId: parseInt(String(parseId(notificationId))) },
      })
      if (result.data?.deleteNotification.success) {
        markNotificationDeleted(
          notificationToDelete?.notificationType,
          Boolean(notificationToDelete && !notificationToDelete.isRead)
        )
        toast.success(intl.formatMessage(N.deleted))
        refetch()
      }
    } catch (err: any) {
      toast.error(intl.formatMessage(N.errorDeleting, {message: err.message}))
    }
  }

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case NotificationType.SIGNATURE_COMPLETED:
        return 'bi bi-check-circle-fill text-success'
      case NotificationType.SIGNATURE_PENDING:
        return 'bi bi-clock-fill text-warning'
      default:
        return 'bi bi-file-earmark-text-fill text-primary'
    }
  }

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'danger'
      case 'HIGH':
        return 'warning'
      case 'NORMAL':
        return 'info'
      case 'LOW':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  return (
    <div
      className='menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-375px'
      data-kt-menu='true'
    >
      <div
        className='d-flex flex-column bgi-no-repeat rounded-top'
        style={{ backgroundImage: `url('${toAbsoluteUrl('/media/misc/menu-header-bg.jpg')}')` }}
      >
        <h3 className='text-white fw-bold px-9 mt-10 mb-6'>
          {intl.formatMessage(N.title)}
          <span className='fs-8 opacity-75 ps-3'>
            {unreadCount > 0
              ? intl.formatMessage(N.unreadCount, {count: unreadCount})
              : intl.formatMessage(N.allRead)}
          </span>
        </h3>

        <ul className='nav nav-line-tabs nav-line-tabs-2x nav-stretch fw-bold px-9'>
          <li className='nav-item'>
            <a
              href='#'
              className={`nav-link text-white opacity-75 opacity-state-100 pb-4 cursor-pointer ${
                activeTab === 'all' ? 'active' : ''
              }`}
              onClick={(e) => {
                e.preventDefault()
                setActiveTab('all')
              }}
            >
              {intl.formatMessage(N.tabAll, {count: signatureNotifications.length})}
            </a>
          </li>
          <li className='nav-item'>
            <a
              href='#'
              className={`nav-link text-white opacity-75 opacity-state-100 pb-4 cursor-pointer ${
                activeTab === 'unread' ? 'active' : ''
              }`}
              onClick={(e) => {
                e.preventDefault()
                setActiveTab('unread')
              }}
            >
              {intl.formatMessage(N.tabUnread, {count: unreadCount})}
            </a>
          </li>
          <li className='nav-item'>
            <a
              href='#'
              className={`nav-link text-white opacity-75 opacity-state-100 pb-4 cursor-pointer ${
                activeTab === 'read' ? 'active' : ''
              }`}
              onClick={(e) => {
                e.preventDefault()
                setActiveTab('read')
              }}
            >
              {intl.formatMessage(N.tabRead, {count: readCount})}
            </a>
          </li>
        </ul>
      </div>

      <div className='tab-content'>
        <div
          className='tab-pane fade show active'
          id='kt_signature_notifications'
          role='tabpanel'
        >
          {loading ? (
            <div className='d-flex justify-content-center align-items-center py-10'>
              <Spinner animation='border' />
            </div>
          ) : (
            <>
              {filteredNotifications.length === 0 ? (
                <div className='d-flex flex-column px-9'>
                  <div className='pt-10 pb-0'>
                    <h3 className='text-dark text-center fw-bolder'>{intl.formatMessage(LOCAL.emptyTitle)}</h3>
                    <div className='text-center text-gray-600 fw-bold pt-1'>
                      {activeTab === 'unread' && intl.formatMessage(LOCAL.emptyUnread)}
                      {activeTab === 'read' && intl.formatMessage(LOCAL.emptyRead)}
                      {activeTab === 'all' && intl.formatMessage(LOCAL.emptyAll)}
                    </div>
                  </div>
                </div>
              ) : (
            <>
              <div className='scroll-y mh-325px my-5 px-8'>
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`d-flex flex-stack py-4 cursor-pointer hover-bg-light-primary rounded px-3 ${
                      !notification.isRead ? 'bg-light-info' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className='d-flex align-items-start flex-grow-1'>
                      <div className='me-3'>
                        <i className={`${getNotificationIcon(notification.notificationType)} fs-2`} />
                      </div>

                      <div className='flex-grow-1'>
                        <div className='d-flex justify-content-between align-items-start mb-1'>
                          <div className='fw-bold text-gray-800 fs-6'>
                            {notification.title}
                            {!notification.isRead && (
                              <span className='ms-2'>
                                <i className='bi bi-circle-fill text-primary' style={{ fontSize: '8px' }} />
                              </span>
                            )}
                          </div>
                          <Badge bg={getPriorityBadge(notification.priority)} className='ms-2'>
                            {priorityLabel(intl, notification.priority)}
                          </Badge>
                        </div>

                        <div className='text-gray-600 fs-7 mb-2'>{notification.message}</div>

                        <div className='d-flex justify-content-between align-items-center'>
                          <span className='text-muted fs-8'>
                            {formatDistanceToNow(new Date(notification.created), {
                              addSuffix: true,
                            })}
                          </span>

                          <button
                            className='btn btn-sm btn-icon btn-light-danger'
                            onClick={(e) => handleDelete(notification.id, e)}
                            title={intl.formatMessage(N.deleteNotification)}
                          >
                            <i className='bi bi-trash fs-6' />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              </>
              )}

              <div className='py-3 text-center border-top'>
                <div className='d-flex justify-content-center gap-3'>
                  {unreadCount > 0 && (
                    <button
                      className='btn btn-sm btn-color-gray-600 btn-active-color-primary'
                      onClick={handleMarkAllAsRead}
                    >
                      {intl.formatMessage(N.markAllRead)}
                    </button>
                  )}
                  <button
                    className='btn btn-sm btn-primary'
                    onClick={() => navigate('/notifications')}
                  >
                    <i className='bi bi-arrows-fullscreen me-1'></i>
                    {intl.formatMessage(N.viewAll)}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export { HeaderSignatureNotificationsMenu }
